import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Chip, Table, TableHead, TableBody, TableRow,
  TableCell, CircularProgress, Divider,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { orderService } from '../services/orderService';
import { WMS_TYPE_CHIP_SX } from '../shared/wmsTypeChip';
import { formatCustomerAddress } from '../shared/formatCustomerAddress';

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  sku: string;
  barcode: string;
  locationCode: string;
  status: string;
}

interface OrderDetail {
  id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  orderType: string;
  status: string;
  priority: number;
  itemCount: number;
  shippingMethod: string;
  createdAt: string;
  cancelNote?: string | null;
  items: OrderItem[];
}

interface StockItem {
  productId: string;
  productName: string;
  requiredQty: number;
  availableQty: number;
  isAvailable: boolean;
  shortage: number;
}

interface OrderDetailModalProps {
  open: boolean;
  orderId: string | null;
  warehouseId: string;
  onClose: () => void;
  onCancelled: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  Pending: 'Bekleyen', AwaitingStock: 'Stok Bekliyor', InWave: 'İş Emrinde', Picking: 'Toplamada',
  Sorting: 'Ayrıştırma', Packing: 'Paketleme', Packed: 'Paketlendi', Shipped: 'Kargoda', Cancelled: 'İptal',
};

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  open, orderId, warehouseId, onClose, onCancelled,
}) => {
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [stockChecked, setStockChecked] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setStockChecked(false);
    setStockItems([]);
    try {
      const res = await orderService.getDetail(orderId);
      const d = res.data?.data || res.data;
      setDetail(d);
    } catch { /* ignore */ }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { if (open && orderId) loadDetail(); }, [open, orderId, loadDetail]);

  const handleStockCheck = async () => {
    if (!orderId || !warehouseId) return;
    setStockLoading(true);
    try {
      const res = await orderService.stockCheck(warehouseId, orderId);
      const items = res.data?.data?.items || res.data?.items || res.data || [];
      setStockItems(Array.isArray(items) ? items : []);
      setStockChecked(true);
    } catch { /* ignore */ }
    setStockLoading(false);
  };

  const handleCancel = async () => {
    if (!orderId) return;
    if (!window.confirm('Bu siparişi iptal etmek istediğinize emin misiniz?')) return;
    setCancelLoading(true);
    try {
      await orderService.cancel(orderId, {
        ReasonCode: 'other',
        ReasonText: 'Sipariş havuzundan iptal',
        Source: 'Pool',
      });
      onCancelled();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'İptal işlemi başarısız');
    }
    setCancelLoading(false);
  };

  const stockMap = new Map(stockItems.map(s => [s.productId, s]));
  const availableCount = stockItems.filter(s => s.isAvailable).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Sipariş Detayı {detail ? `- ${detail.orderNumber}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : detail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Müşteri</Typography>
                <Typography variant="body2" fontWeight={500}>{detail.customerName}</Typography>
              </Box>
              {detail.customerPhone && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Telefon</Typography>
                  <Typography variant="body2">{detail.customerPhone}</Typography>
                </Box>
              )}
              {detail.customerAddress && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Adres</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 420 }}>
                    {formatCustomerAddress(detail.customerAddress)}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Tip</Typography>
                <Box><Chip label={detail.orderType === 'Single' ? 'Tekli' : 'Çoklu'} size="small"
                  color={detail.orderType === 'Single' ? 'info' : 'secondary'} sx={WMS_TYPE_CHIP_SX} /></Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Durum</Typography>
                <Box><Chip label={STATUS_LABELS[detail.status] || detail.status} size="small" color="primary" /></Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Öncelik</Typography>
                <Typography variant="body2">{detail.priority > 0 ? `Yüksek (${detail.priority})` : 'Normal'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Oluşturulma</Typography>
                <Typography variant="body2">{new Date(detail.createdAt).toLocaleDateString('tr-TR')}</Typography>
              </Box>
            </Box>

            {detail.status === 'AwaitingStock' && detail.cancelNote && (
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary">Stok bekleme nedeni</Typography>
                <Typography variant="body2">{detail.cancelNote}</Typography>
              </Box>
            )}

            <Divider />
            <Typography variant="subtitle2">Ürünler</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ürün Adı</TableCell>
                  <TableCell align="center">Miktar</TableCell>
                  {stockChecked && <TableCell align="center">Stok</TableCell>}
                  <TableCell>Lokasyon</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detail.items || []).map(item => {
                  const stock = stockMap.get(item.productId);
                  return (
                    <TableRow key={item.id}>
                      <TableCell>{item.productName || item.sku || item.barcode}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      {stockChecked && (
                        <TableCell align="center">
                          {stock ? (
                            stock.isAvailable ? (
                              <Chip icon={<CheckCircleIcon />} label={stock.availableQty}
                                size="small" color="success" variant="outlined" sx={{ height: 24 }} />
                            ) : (
                              <Chip icon={<WarningIcon />}
                                label={`${stock.availableQty} (eksik: ${stock.shortage})`}
                                size="small" color="error" variant="outlined" sx={{ height: 24 }} />
                            )
                          ) : (
                            <Chip label="?" size="small" color="default" variant="outlined" sx={{ height: 24 }} />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.locationCode || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {stockChecked && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Stok Durumu: {availableCount}/{stockItems.length} ürün stokta
                {availableCount === stockItems.length ? (
                  <Chip label="Tamamı Mevcut" size="small" color="success" sx={{ ml: 1, height: 20 }} />
                ) : (
                  <Chip label="Eksik Var" size="small" color="error" sx={{ ml: 1, height: 20 }} />
                )}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">Yükleniyor...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleStockCheck} disabled={stockLoading || !detail}>
          {stockLoading ? <CircularProgress size={18} /> : 'Stok Kontrol Et'}
        </Button>
        {detail && (detail.status === 'Pending' || detail.status === 'InWave' || detail.status === 'Picking') && (
          <Button variant="outlined" color="error" onClick={handleCancel} disabled={cancelLoading}>
            {cancelLoading ? <CircularProgress size={18} /> : 'İptal Et'}
          </Button>
        )}
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailModal;
